import React, { FC } from 'react';
import { FastField, FieldProps } from 'formik';
import { FormHelperText, TextField } from '@mui/material';
import { format } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers';

interface FormFieldDateProps {
  label: string;
  name: string;
  readonly?: boolean;
  disableFuture?: boolean;
  disablePast?: boolean;
  autoFocus?: boolean;
  maxDate?: Date;
  minDate?: Date;
}

const isValidDate = (d: unknown) => {
  if (Object.prototype.toString.call(d) !== '[object Date]') return false;
  const date = d as Date;
  return !Number.isNaN(date.getTime());
};

const FormFieldDate: FC<FormFieldDateProps> = ({
  label,
  name,
  readonly,
  disableFuture,
  disablePast,
  autoFocus,
  maxDate,
  minDate,
}: FormFieldDateProps) => (
  <FastField name={name}>
    {({ field, meta, form }: FieldProps) => (
      <>
        <DatePicker
          label={label}
          disableFuture={disableFuture}
          disablePast={disablePast}
          value={field.value ?? null}
          onChange={(newValue: Date | null) => {
            if (newValue != null && isValidDate(newValue) && newValue.getFullYear() > 1900) {
              form.setFieldValue(name, format(newValue, 'yyyy-MM-dd'));
              return;
            }
            // For invalid or out-of-range dates, clear the field explicitly
            form.setFieldValue(name, null);
          }}
          disabled={readonly}
          disableMaskedInput
          inputFormat="d.M.yyyy"
          autoFocus={autoFocus}
          maxDate={maxDate}
          minDate={minDate}
          openTo="day"
          views={['year', 'month', 'day']}
          renderInput={
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (params: any) => (
              <TextField
                size="small"
                {...params}
                error={!!meta.error && meta.touched}
                helperText={(!!meta.error && meta.touched && meta.error) ?? params?.helperText}
              />
            )
          }
        />
        {meta.error && meta.touched && (
          <FormHelperText sx={{ pl: 2 }} error>
            {meta.error}
          </FormHelperText>
        )}
      </>
    )}
  </FastField>
);

FormFieldDate.displayName = 'FormFieldDate';

export default FormFieldDate;
